import { Injectable, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { merge } from 'rxjs';
import { map } from 'rxjs/operators';
import { VideoPlayerConfigService } from '@pcp/streaming';
import { ClassroomEffectsHelperService } from 'shared/classroom/data-access';
import {
    ClassroomConfig,
    PCP_CLASSROOM_SYNC_CONFIG,
} from 'shared/classroom/data-access';
// NOTE: the config-token import path moves to 'shared/classroom/data-access' in Part 2.

/**
 * Replicates the gating that `ClassroomSignalrEmitterEffects` performed before its
 * relay effects were inlined into `SlidePreviewFacade` (Plan 06 / Part 1).
 *
 * `SlidePreviewFacade` runs on teacher, beamer AND live-lesson clients. The deleted
 * emitter effects only forwarded SignalR sends where the client was configured as an
 * emitter, had joined a lesson, and the hub was connected — plus per-channel flags.
 * Without these guards an inlined `LessonCommandFacade.broadcast.*` call would fire on
 * every client, causing echo loops / duplicate sends.
 *
 * Mapping to the old gates:
 *  - `emitterReady`      ← class-wide `ngrxOnRunEffects`: `config.isEmitter` AND
 *                          `ClassroomEffectsHelperService.hasLessonIdAndIsConnected$`.
 *  - `canEmitRubber`     ← + `filter(config.emitSlideMutations === true)`.
 *  - `canEmitVideo`      ← + `switchMap(emitVideoChanges$ true) / takeUntil(false)` over the
 *                          DYNAMIC `VideoPlayerConfigService.emitVideoChanges$` stream
 *                          (NOT the static `config.emitVideoChanges` flag).
 *  - `canEmitPerception` ← class-wide only.
 *  - `canSendIntent`     ← class-wide only (the per-intent `throttleTime(500)` is replicated
 *                          at the call site in `SlidePreviewFacade`).
 *
 * Every member is a `computed` so it re-reads its inputs on each call; a stored boolean
 * would freeze at construction time.
 */
@Injectable()
export class ClassroomEmitGateService {
    private readonly config = inject<ClassroomConfig>(PCP_CLASSROOM_SYNC_CONFIG);
    private readonly helper = inject(ClassroomEffectsHelperService);
    private readonly videoPlayerConfig = inject(VideoPlayerConfigService);

    /**
     * Replicates `ngrxOnRunEffects`'s connection gate: opens once the lesson is joined and
     * the hub is connected, stays open across transient hub blips (the old gate had no
     * `takeUntil` on disconnect), and closes when the lesson is left (`hasNoLessonId$`).
     * Starts `false` synchronously, matching effects that hadn't started pre-join.
     */
    private readonly connected = toSignal(
        merge(
            this.helper.hasLessonIdAndIsConnected$.pipe(map(() => true)),
            this.helper.hasNoLessonId$.pipe(map(() => false)),
        ),
        { initialValue: false },
    );

    private readonly videoEmitEnabled = toSignal(this.videoPlayerConfig.emitVideoChanges$, {
        initialValue: false,
    });

    readonly emitterReady = computed(() => this.config.isEmitter && this.connected());
    readonly canEmitRubber = computed(
        () => this.emitterReady() && this.config.emitSlideMutations === true,
    );
    readonly canEmitVideo = computed(() => this.emitterReady() && this.videoEmitEnabled());
    readonly canEmitPerception = this.emitterReady;
    readonly canSendIntent = this.emitterReady;
}
